import { Controller } from '@hotwired/stimulus';

const AUDIENCES = [
  'First-time SaaS founders',
  'Night-shift nurses',
  'Wedding photographers',
  'HOA board members',
  'Youth sports parents',
  'Food truck operators',
  'Indie podcasters',
  'Band tour managers',
  'Coffee shop managers',
  'Substitute teachers',
  'Freelance motion designers',
  'Rescue-foster coordinators',
  'Small church treasurers',
  'Etsy shop owners',
  'Barbers with chair renters',
  'Mobile pet groomers',
  'Short-term rental hosts',
  'CrossFit gym owners',
  'Newsletter writers (under 5k subs)',
  'Bootcamp career switchers',
  'Film festival volunteers',
  'Wedding DJs',
  'Pop-up market vendors',
  'High school club advisors',
  'Twitch streamers (small)',
  'Accountants moonlighting',
  'Nonprofit grant writers',
  'Plant shop owners',
  'Parent-teacher org treasurers',
  'Solo lawyers (flat-fee niches)',
  'Home organizers',
  'Voice actors between gigs',
  'Camp counselors / trip leads',
  'Sourdough-tier recipe bloggers',
];

const PAINS = [
  'Chase late invoices without sounding mean',
  'Prove ROI to a skeptical partner',
  'Stop double-booking the van or booth',
  'Compare three quotes without a spreadsheet headache',
  'See cash 8 weeks out before panic',
  'Track who said "maybe" and stopped replying',
  'Plan content without burning out Sunday night',
  'Remember who borrowed gear (and which cable)',
  'Spot the one SKU eating all margin',
  'Survive inventory week without tears',
  'Know which sponsor slot is actually worth it',
  'Turn chaos RSVPs into a headcount',
  'Ship a launch without forgetting a task',
  'Keep volunteers from ghosting mid-project',
  'Know which lead is hot before you call',
  'Stop losing receipts in camera roll hell',
  'Explain fees to clients in one screenshot',
  'Pick the next experiment without debate fatigue',
  'See which day of week actually sells',
  'Stop re-typing the same email 40 times',
  'Track habits clients actually stick to',
  'Balance fairness when splitting gig pay',
  'Know when to fire a painful client',
  'Ship a clean handoff to the next shift',
  'Spot scope creep before it invoices you',
  'Remember renewal dates before they bite',
  'Prioritize when everything is "urgent"',
  'Make a budget feel like a game, not a lecture',
  'Track trial users before they go cold',
  'See which marketing channel is lying to you',
  'Keep a family business out of group-chat doom',
];

const STYLES = [
  'Filterable CRM directory',
  'One-tab weekly command center',
  '30-day sprint checklist with owners',
  'What-if calculator (sliders welcome)',
  'Swipe-file grid with YOUR notes column',
  'Simple scoreboard + trend sparkline vibes',
  'Print-friendly one-pager summary tab',
  "Kanban-lite (status = dropdown, don't overthink)",
  'Roster + hours + payout snapshot',
  'Pipeline + next-touch reminder column',
  'Inventory in / out with low-stock alerts',
  'Content calendar + asset links in cells',
  'Budget vs actual with gentle color highlights',
  'Meeting notes that roll up to decisions',
  'Quote compare matrix with winner row',
  'Habit tracker that does not shame anyone',
  'Event run-of-show minute by minute',
  'Simple forecast: inputs in, panic out',
  'Feedback inbox triage with tags',
  'Template pack: 3 tabs, same branding',
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default class extends Controller {
  static targets = ['audience', 'pain', 'style', 'button', 'formula', 'shell'];

  connect() {
    this.spinning = false;
    this._tickId = null;
    if (!this.audienceTarget.textContent.trim()) {
      this.audienceTarget.textContent = 'Hit spin';
      this.painTarget.textContent = 'Hit spin';
      this.styleTarget.textContent = 'Hit spin';
    }
    this._syncFormula();
  }

  disconnect() {
    this._clearSpin();
  }

  spin() {
    if (this.spinning) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      this._settle();
      return;
    }

    this.spinning = true;
    this.buttonTarget.disabled = true;
    this.shellTarget.classList.add('ideas-slot--spinning');
    this._clearSpin();

    const totalTicks = 42 + Math.floor(Math.random() * 14);
    let tick = 0;

    const step = () => {
      this.audienceTarget.textContent = pick(AUDIENCES);
      this.painTarget.textContent = pick(PAINS);
      this.styleTarget.textContent = pick(STYLES);
      this._syncFormula();

      tick += 1;
      if (tick >= totalTicks) {
        this._settle();
        return;
      }
      const delay = Math.min(28 + tick * 4, 120);
      this._tickId = window.setTimeout(step, delay);
    };

    step();
  }

  _settle() {
    this._clearSpin();
    this.audienceTarget.textContent = pick(AUDIENCES);
    this.painTarget.textContent = pick(PAINS);
    this.styleTarget.textContent = pick(STYLES);
    this._syncFormula();
    this.shellTarget.classList.remove('ideas-slot--spinning');
    this.spinning = false;
    this.buttonTarget.disabled = false;
  }

  _syncFormula() {
    if (!this.hasFormulaTarget) return;
    const a = this.audienceTarget.textContent.replace(/"/g, '""');
    const b = this.painTarget.textContent.replace(/"/g, '""');
    const c = this.styleTarget.textContent.replace(/"/g, '""');
    this.formulaTarget.textContent = `=CONCATENATE("${a}", " → ", "${b}", " → ", "${c}")`;
  }

  _clearSpin() {
    if (this._tickId != null) {
      window.clearTimeout(this._tickId);
      this._tickId = null;
    }
  }
}
